<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useWorkspaceSettingsStore } from '@/modules/workspace/stores/workspace-settings-store'
import WorkspaceSettingsLayout from '@/modules/workspace/components/workspace-settings-layout.vue'
import WorkspaceNav from '@/modules/workspace/components/workspace-nav.vue'
import TheLayout from '@/modules/shared/layouts/the-layout.vue'
import { RouterLinkBack, VButton, VButtonInvisible, VToggleButtonField } from '@/modules/shared/components'

const workspaceSettingsStore = useWorkspaceSettingsStore()

const selectedTab = computed(() => 'email_notifications' as const)

const form = ref({
  notify_when_investor_registers: false,
  notify_when_company_begins: false,
  notify_when_company_applies: false,
})

const setForm = () => {
  const settings = workspaceSettingsStore.settings
  if (settings) {
    form.value = {
      notify_when_investor_registers: settings.notify_when_investor_registers ?? false,
      notify_when_company_begins: settings.notify_when_company_begins ?? false,
      notify_when_company_applies: settings.notify_when_company_applies ?? false,
    }
  }
}

watch(() => workspaceSettingsStore.settings, setForm, { immediate: true })

const submit = async () => {
  await workspaceSettingsStore.updateSettings(form.value)
}

onMounted(async () => {
  await workspaceSettingsStore.fetch()
})
</script>

<template>
  <TheLayout>
    <div class="-mt-8 mb-6">
      <RouterLinkBack class="text-lg" :to="{ name: 'dashboard' }">
        <VButtonInvisible>← Back</VButtonInvisible>
      </RouterLinkBack>
    </div>

    <WorkspaceNav />

    <WorkspaceSettingsLayout :selected-tab="selectedTab">
      <form @submit.prevent="submit" class="space-y-6">
        <!-- Email Notifications Section -->
        <div class="text-lg font-semibold">Email Notifications</div>
        <p class="text-sm text-gray-500">Receive email notifications when the following events occur</p>

        <VToggleButtonField
          v-model="form.notify_when_investor_registers"
          label="Investor registration"
          description="Notify group admins when a new investor registers."
          trueLabel="Enable"
          falseLabel="Disable"
        />

        <VToggleButtonField
          v-model="form.notify_when_company_begins"
          label="Application started"
          description="Notify group admins when a company begins the application process."
          trueLabel="Enable"
          falseLabel="Disable"
        />

        <VToggleButtonField
          v-model="form.notify_when_company_applies"
          label="Application submitted"
          description="Notify group admins when a company submits their application."
          trueLabel="Enable"
          falseLabel="Disable"
        />

        <div class="pt-4">
          <VButton variant="v-blue" size="lg" type="submit" :loading="workspaceSettingsStore.loading">
            Save Changes
          </VButton>
        </div>
      </form>
    </WorkspaceSettingsLayout>
  </TheLayout>
</template>
